/**
 * O destaque grande do guia — no topo de Treinamentos e do índice do guia.
 *
 * É o ÚNICO bloco violeta da tela (DESIGN_SYSTEM §1.5: uma cor de destaque só), com a única
 * sombra de marca permitida. A captura à direita não é enfeite: mostra, antes do clique, que o
 * guia é feito de telas de verdade.
 */
import { Link } from 'react-router';
import { ArrowRight, BookOpenCheck, Images, ListChecks } from 'lucide-react';
import { cn } from '@mgdlead/ui';

import { contarPassos } from './busca';
import { CAPITULOS, caminhoDoGuia } from './conteudo';
import { ProgressoDoGuia } from './componentes';

function contarImagens(): number {
  let n = 0;
  for (const c of CAPITULOS) {
    if (c.capa) n += 1;
    for (const s of c.secoes) for (const p of s.passos) if (p.imagem) n += 1;
  }
  return n;
}

export function GuiaDestaque({
  slug,
  lidos,
  mostrarIndice = true,
}: {
  slug: string;
  lidos: ReadonlySet<string>;
  /** No próprio índice, o botão "Ver todos os capítulos" não faz sentido. */
  mostrarIndice?: boolean;
}) {
  const total = CAPITULOS.length;
  const lidosNoGuia = CAPITULOS.filter((c) => lidos.has(c.id)).length;
  const proximo = CAPITULOS.find((c) => !lidos.has(c.id)) ?? CAPITULOS[0];
  const comecou = lidosNoGuia > 0;
  const topicos = CAPITULOS.reduce((t, c) => t + contarPassos(c), 0);

  return (
    <section
      aria-labelledby="guia-destaque-titulo"
      className="guia-sobe relative overflow-hidden rounded-xl bg-primary text-primary-foreground shadow-brand"
    >
      <div className="grid gap-6 p-5 md:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] md:gap-4 md:p-8">
        <div className="flex min-w-0 flex-col gap-5">
          <div className="flex flex-col gap-2">
            <p className="font-mono text-2xs uppercase tracking-caps text-violet-100">
              Guia do MGDLead
            </p>
            <h2 id="guia-destaque-titulo" className="text-2xl md:text-3xl">
              O painel inteiro, tela por tela
            </h2>
            <p className="max-w-prose text-base text-violet-100">
              O primeiro acesso, as conexões, cada aba e cada número explicados com imagens da tela
              de verdade — e o que fazer quando um número está ruim.
            </p>
          </div>

          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-violet-50">
            <li className="inline-flex items-center gap-1.5">
              <BookOpenCheck aria-hidden="true" className="size-4" />
              <span className="nums">{total}</span> capítulos
            </li>
            <li className="inline-flex items-center gap-1.5">
              <ListChecks aria-hidden="true" className="size-4" />
              <span className="nums">{topicos}</span> tópicos
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Images aria-hidden="true" className="size-4" />
              <span className="nums">{contarImagens()}</span> imagens
            </li>
          </ul>

          <div className="max-w-sm">
            <ProgressoDoGuia lidos={lidosNoGuia} total={total} invertido />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {proximo ? (
              <Link
                to={caminhoDoGuia(slug, proximo.id)}
                className={cn(
                  'inline-flex min-h-11 items-center justify-center gap-2 rounded-pill bg-surface px-5',
                  'text-base font-medium text-primary-text transition-opacity hover:opacity-90',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary',
                )}
              >
                {comecou ? `Continuar: ${proximo.titulo}` : 'Começar pelos primeiros passos'}
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            ) : null}
            {mostrarIndice ? (
              <Link
                to={caminhoDoGuia(slug)}
                className={cn(
                  'inline-flex min-h-11 items-center justify-center rounded-pill px-5 text-base font-medium',
                  'text-primary-foreground ring-1 ring-inset ring-violet-300 hover:bg-primary-hover',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground',
                )}
              >
                Ver todos os capítulos
              </Link>
            ) : null}
          </div>
        </div>

        {/* A tela de verdade, cortada pela borda: "isto aqui é o que você vai aprender". */}
        <div aria-hidden="true" className="relative hidden min-h-64 md:block">
          <div className="absolute -bottom-12 -right-16 left-4 top-2 overflow-hidden rounded-lg ring-4 ring-violet-400">
            <img
              src="/guia/dashboard.webp"
              alt=""
              width={1280}
              height={800}
              className="size-full object-cover object-left-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
